import React, {
  createContext,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { Box, Button, CircularProgress } from '@mui/material';

import { useDelayedBoolean, useIsMounted } from '@paragon/client-tools';

import { useErrorReporter } from '../ErrorBarrier';
import { LoginTypography } from './LoginTypography';

interface Props {
  readonly children?: ReactNode;
  readonly disabled?: boolean;
  readonly autoFocusSubmit?: boolean;

  readonly submitText: string;
  readonly spinnerText?: string;

  readonly onSubmit: () => void | Promise<void>;
  readonly onSubmissionWillBegin?: () => void | Promise<void>;
  readonly onSubmissionDidEnd?: () => void | Promise<void>;
}

// A context is used to automatically hook up the `onSubmit` event for all `LoginInput` components within this form.
const OnSubmitContext = createContext<(event: MouseEvent | KeyboardEvent) => void>(() => {});
export const useOnSubmit = () => useContext(OnSubmitContext);

/**
 * This component is used as a container for forms used in the various login-related panels, providing a standard
 *   approach to container styling, submission handling, error handling, and progress indication.
 */
export function LoginForm(props: Props) {
  const { onSubmit: onSubmitWithoutErrorHandling, onSubmissionWillBegin, onSubmissionDidEnd } = props;

  const reportCaughtError = useErrorReporter();

  const stillMounted = useIsMounted();
  const [isSubmitting, hasSpinner, setIsSubmitting] = useDelayedBoolean(false);

  // For some reason the autoFocus attribute is not working on buttons, so we have to manually implement it using a ref.
  const submitButton = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (props.autoFocusSubmit) {
      submitButton.current?.focus();
    }
  }, [props.autoFocusSubmit]);

  // We need to wrap the `onSubmit` event to hook up error handling.
  const onSubmit = useCallback(
    async (event: MouseEvent | KeyboardEvent) => {
      event.nativeEvent.preventDefault(); // ensure form is not submitted causing a page reload

      setIsSubmitting(true);
      onSubmissionWillBegin?.();

      try {
        await onSubmitWithoutErrorHandling();
      } catch (error) {
        reportCaughtError(error);
      } finally {
        if (stillMounted()) {
          onSubmissionDidEnd?.();
          setIsSubmitting(false);
        }
      }
    },
    [
      onSubmissionDidEnd,
      onSubmissionWillBegin,
      onSubmitWithoutErrorHandling,
      reportCaughtError,
      setIsSubmitting,
      stillMounted,
    ],
  );

  return (
    <OnSubmitContext.Provider value={onSubmit}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexWrap: 'wrap',
          width: '100%', // Fix IE11 issue.
          mt: 1,
        }}
      >
        {hasSpinner ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <GradientCircularProgress />
            {props.spinnerText ? (
              <Box sx={{ mt: 2, width: '100%' }}>
                <LoginTypography>{props.spinnerText}</LoginTypography>
              </Box>
            ) : null}
          </Box>
        ) : (
          props.children
        )}
      </Box>
      {hasSpinner ? null : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            mb: { xs: 0, md: 2 },
            mt: { xs: 1, md: 5 },
          }}
        >
          <Button
            id="login"
            color="primary"
            variant="contained"
            fullWidth
            onClick={onSubmit}
            ref={submitButton}
            disabled={isSubmitting || props.disabled}
          >
            {props.submitText}
          </Button>
        </Box>
      )}
    </OnSubmitContext.Provider>
  );
}

// From https://mui.com/material-ui/react-progress/#customization
function GradientCircularProgress() {
  return (
    <React.Fragment>
      <svg width={0} height={0}>
        <defs>
          <linearGradient id="my_gradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#F4D167" />
            <stop offset="100%" stopColor="#4D9378" />
          </linearGradient>
        </defs>
      </svg>
      <CircularProgress sx={{ 'svg circle': { stroke: 'url(#my_gradient)' } }} />
    </React.Fragment>
  );
}
